import React, { ChangeEvent, useMemo, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Typography,
  Modal,
} from "antd";
import {
  CourseEntityInput,
  NemPrimaryBook,
  NemPrimaryLinkingItem,
  RelevantCourseItem,
} from "../../../../services/courses/courses.types";
import { LessonMinimalResponse } from "../../../../types/api/getLesson";
import { CourseMinimalResponse } from "../../../../types/api/getCourse";
import { ProgramEntity } from "../../../../services/programs/programs.types";
import { EJES_ARTICULADORES_COURSE } from "../constants";
import CourseLessonsSelector from "./CourseLessonsSelector";

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

interface LessonSelected {
  id: number;
  title: string;
  _uid?: string;
}

interface NormalCourseFormProps {
  initialValues: CourseEntityInput;
  setInitialValues: React.Dispatch<React.SetStateAction<CourseEntityInput>>;
  getEvaluationFieldsValidation: () => any[];
  tagsSelected: string[];
  handleChangeTags: (values: string[]) => void;
  selectedLessons: LessonSelected[];
  availableLessons: LessonMinimalResponse[];
  handleRemoveLesson: (index: number) => void;
  handleAddLessonAtIndex: (index: number) => void;
  handleLessonChange: (index: number, field: string, value: any) => void;
  handleReorderLessons: (lessons: LessonSelected[]) => void;

  allCourses: CourseMinimalResponse[];
  relevantCourses: RelevantCourseItem[];
  setRelevantCourses: (items: RelevantCourseItem[]) => void;
  programs: ProgramEntity[];
  currentCourseId?: number;
  nemPrimaryLinkings: NemPrimaryLinkingItem[];
  setNemPrimaryLinkings: React.Dispatch<
    React.SetStateAction<NemPrimaryLinkingItem[]>
  >;
}

const NormalCourseForm: React.FC<NormalCourseFormProps> = ({
  initialValues,
  setInitialValues,
  getEvaluationFieldsValidation,
  tagsSelected,
  handleChangeTags,
  selectedLessons,
  availableLessons,
  handleRemoveLesson,
  handleAddLessonAtIndex,
  handleLessonChange,
  handleReorderLessons,
  allCourses,
  relevantCourses,
  setRelevantCourses,
  programs,
  currentCourseId,
  nemPrimaryLinkings,
  setNemPrimaryLinkings,
}) => {
  const [courseToAdd, setCourseToAdd] = useState<number | undefined>(undefined);
  const [programToAdd, setProgramToAdd] = useState<number | undefined>(
    undefined,
  );
  const [isRelevantModalOpen, setIsRelevantModalOpen] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState<number | undefined>(
    undefined,
  );

  const selectableCourses = useMemo(() => {
    return allCourses
      .filter((c) => c.id !== currentCourseId)
      .filter(
        (c) =>
          !relevantCourses.some((r) => r.course_id === c.id) ||
          c.id === editingCourseId,
      )
      .sort((a, b) => b.id - a.id);
  }, [allCourses, currentCourseId, relevantCourses, editingCourseId]);

  const selectedCourses = useMemo(() => {
    const byId = new Map(allCourses.map((c) => [c.id, c] as const));
    return relevantCourses.map((r) => ({
      ...r,
      course: byId.get(r.course_id) || {
        id: r.course_id,
        title: `Curso ${r.course_id}`,
      },
    }));
  }, [allCourses, relevantCourses]);

  const programsForSelectedCourse = useMemo(() => {
    if (!courseToAdd) return [];
    const course = allCourses.find((c) => c.id === courseToAdd);
    const ids = course?.program_ids ?? [];
    return programs.filter((p) => ids.includes(p.id));
  }, [courseToAdd, allCourses, programs]);

  const programById = useMemo(() => {
    return new Map(programs.map((p) => [p.id, p] as const));
  }, [programs]);

  const canAddMore = relevantCourses.length < 3;

  const handleSaveRelevantCourse = () => {
    if (!courseToAdd || !programToAdd) return;
    if (editingCourseId !== undefined) {
      setRelevantCourses(
        relevantCourses.map((r) =>
          r.course_id === editingCourseId
            ? { course_id: courseToAdd, program_id: programToAdd }
            : r,
        ),
      );
    } else {
      if (!canAddMore) return;
      setRelevantCourses([
        ...relevantCourses,
        { course_id: courseToAdd, program_id: programToAdd },
      ]);
    }
    setCourseToAdd(undefined);
    setProgramToAdd(undefined);
    setEditingCourseId(undefined);
    setIsRelevantModalOpen(false);
  };

  const handleEditRelevantCourse = (
    courseId: number,
    programId: number | null | undefined,
  ) => {
    setCourseToAdd(courseId);
    setProgramToAdd(programId ?? undefined);
    setEditingCourseId(courseId);
    setIsRelevantModalOpen(true);
  };

  const handleRemoveRelevantCourse = (courseId: number) => {
    setRelevantCourses(relevantCourses.filter((r) => r.course_id !== courseId));
  };

  return (
    <>
      {/* Aprendizajes clave y Rúbricas */}
      <Form.Item
        label="Aprendizajes clave"
        name="key_learnings"
        rules={getEvaluationFieldsValidation()}
      >
        <TextArea
          rows={3}
          value={initialValues.key_learnings}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setInitialValues({
              ...initialValues,
              key_learnings: e.target.value,
            })
          }
        />
      </Form.Item>

      <div className="rubricSection">
        <div className="rubricRow">
          <div className="rubricItem">
            <div className="rubricLabel">Rúbrica: logrado</div>
            <Form.Item
              name="rubric_achieved"
              rules={getEvaluationFieldsValidation()}
              style={{ marginBottom: 0 }}
            >
              <TextArea
                rows={4}
                value={initialValues.rubric_achieved}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setInitialValues({
                    ...initialValues,
                    rubric_achieved: e.target.value,
                  })
                }
              />
            </Form.Item>
          </div>
          <div className="rubricItem">
            <div className="rubricLabel">Rúbrica: en proceso</div>
            <Form.Item
              name="rubric_in_process"
              rules={getEvaluationFieldsValidation()}
              style={{ marginBottom: 0 }}
            >
              <TextArea
                rows={4}
                value={initialValues.rubric_in_process}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setInitialValues({
                    ...initialValues,
                    rubric_in_process: e.target.value,
                  })
                }
              />
            </Form.Item>
          </div>
          <div className="rubricItem">
            <div className="rubricLabel">Rúbrica: requiere apoyo</div>
            <Form.Item
              name="rubric_needs_support"
              rules={getEvaluationFieldsValidation()}
              style={{ marginBottom: 0 }}
            >
              <TextArea
                rows={4}
                value={initialValues.rubric_needs_support}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setInitialValues({
                    ...initialValues,
                    rubric_needs_support: e.target.value,
                  })
                }
              />
            </Form.Item>
          </div>
        </div>
      </div>

      {initialValues.subject_id !== 5 && initialValues.subject_id !== 4 && (
        <>
          <Form.Item label="Contenido" name="content">
            <TextArea
              rows={3}
              value={initialValues.content}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  content: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item label="PDAs" name="pda">
            <TextArea
              rows={3}
              value={initialValues.pda}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  pda: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item
            label="Metas de aprendizaje del curso"
            name="learning_goals"
          >
            <TextArea
              rows={3}
              value={initialValues.learning_goals}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  learning_goals: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item label="Vinculación NEM Kinder" name="book_linking_nem">
            <TextArea
              rows={3}
              value={initialValues.book_linking_nem}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  book_linking_nem: e.target.value,
                })
              }
            />
          </Form.Item>
        </>
      )}

      <Form.Item label="Ejes articuladores" name="ejes_articuladores">
        <Select
          mode="multiple"
          value={initialValues.ejes_articuladores || []}
          onChange={(values: string[]) =>
            setInitialValues({
              ...initialValues,
              ejes_articuladores: values,
            })
          }
        >
          {EJES_ARTICULADORES_COURSE.map((eje) => (
            <Option key={eje} value={eje}>
              {eje}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* LITERACY */}
      {initialValues.subject_id === 5 && (
        <>
          <Form.Item label="Habilidad" name="ability">
            <TextArea
              rows={3}
              value={initialValues.ability}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  ability: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item label="Metas de trazo" name="stroke_goals">
            <TextArea
              rows={3}
              value={initialValues.stroke_goals}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  stroke_goals: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item
            label="Metas silábicas/fonéticas"
            name="syllabic_phonetic_goals"
          >
            <TextArea
              rows={3}
              value={initialValues.syllabic_phonetic_goals}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  syllabic_phonetic_goals: e.target.value,
                })
              }
            />
          </Form.Item>
        </>
      )}

      {/* ENGLISH */}
      {initialValues.subject_id === 4 && (
        <>
          <Form.Item label="Communication Goals" name="communication_goals">
            <TextArea
              rows={3}
              value={initialValues.communication_goals}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  communication_goals: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item label="Teacher Structures" name="structures">
            <TextArea
              rows={3}
              value={initialValues.structures}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  structures: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item label="Student Structures" name="student_structures">
            <TextArea
              rows={3}
              value={initialValues.student_structures}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  student_structures: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item label="Vocabulary: New Words" name="vocabulary_new_words">
            <TextArea
              rows={3}
              value={initialValues.vocabulary_new_words}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  vocabulary_new_words: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item
            label="Vocabulary: Review Words"
            name="vocabulary_review_words"
          >
            <TextArea
              rows={3}
              value={initialValues.vocabulary_review_words}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  vocabulary_review_words: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item
            label="Phonemics / Reading Goals"
            name="phonemics_reading_goals"
          >
            <TextArea
              rows={3}
              value={initialValues.phonemics_reading_goals}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setInitialValues({
                  ...initialValues,
                  phonemics_reading_goals: e.target.value,
                })
              }
            />
          </Form.Item>
        </>
      )}

      {/* Tags */}
      <Form.Item label="Tags">
        <Select
          mode="tags"
          style={{ width: "100%" }}
          placeholder="Escribe y presiona Enter"
          value={tagsSelected}
          onChange={handleChangeTags}
        />
      </Form.Item>

      {/* Lecciones */}
      <CourseLessonsSelector
        selectedLessons={selectedLessons}
        availableLessons={availableLessons}
        handleRemoveLesson={handleRemoveLesson}
        handleAddLessonAtIndex={handleAddLessonAtIndex}
        handleLessonChange={handleLessonChange}
        handleReorderLessons={handleReorderLessons}
      />

      {/* Cursos relevantes */}
      <div className="selectRow" style={{ marginTop: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Title level={4} style={{ marginBottom: 0 }}>
            Cursos relevantes
          </Title>
          <Typography.Text type="secondary">
            {relevantCourses.length}/3
          </Typography.Text>
        </div>

        {selectedCourses.length > 0 ? (
          <div style={{ marginTop: 12 }}>
            {selectedCourses.map(({ course, program_id }) => {
              const program = program_id
                ? programById.get(program_id)
                : undefined;

              return (
                <div
                  key={course.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 12px",
                    borderBottom: "1px solid var(--bo-card-border)",
                  }}
                >
                  <div
                    style={{ display: "flex", gap: 16, alignItems: "center" }}
                  >
                    <div style={{ minWidth: 260 }}>
                      {course.id} {course.title}
                    </div>
                    {program ? (
                      <div style={{ color: "#999" }}>
                        {program.program_code}
                      </div>
                    ) : null}
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      type="text"
                      onClick={() =>
                        handleEditRelevantCourse(course.id, program_id)
                      }
                    >
                      Editar
                    </Button>
                    <Button
                      type="text"
                      danger
                      onClick={() => handleRemoveRelevantCourse(course.id)}
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        <div
          style={{
            marginTop: 12,
            border: "1px dashed var(--bo-border-strong)",
            borderRadius: 8,
            padding: 16,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: canAddMore ? "pointer" : "not-allowed",
            opacity: canAddMore ? 1 : 0.5,
          }}
          onClick={() => {
            if (!canAddMore) return;
            setIsRelevantModalOpen(true);
          }}
        >
          <Typography.Text strong>Agregar cursos</Typography.Text>
        </div>

        <Modal
          title={
            editingCourseId !== undefined
              ? "Editar curso relevante"
              : `Agregar curso relevante (${relevantCourses.length}/3)`
          }
          open={isRelevantModalOpen}
          onCancel={() => {
            setIsRelevantModalOpen(false);
            setCourseToAdd(undefined);
            setProgramToAdd(undefined);
            setEditingCourseId(undefined);
          }}
          okText={editingCourseId !== undefined ? "Guardar" : "Agregar"}
          onOk={handleSaveRelevantCourse}
          okButtonProps={{
            disabled:
              !courseToAdd ||
              !programToAdd ||
              (editingCourseId === undefined && !canAddMore),
          }}
          cancelText="Cancelar"
        >
          <Select
            showSearch
            placeholder="Buscar curso"
            value={courseToAdd}
            onChange={(value) => {
              setCourseToAdd(value);
              setProgramToAdd(undefined);
            }}
            style={{ width: "100%" }}
            optionFilterProp="label"
            disabled={!canAddMore}
            options={selectableCourses.map((c) => ({
              value: c.id,
              label: `${c.id} - ${c.title}`,
            }))}
          />
          <div style={{ marginTop: 16 }}>
            <Typography.Text strong>Programa asociado</Typography.Text>
            <Select
              placeholder="Seleccionar programa"
              value={programToAdd}
              onChange={(value) => setProgramToAdd(value)}
              style={{ width: "100%", marginTop: 8 }}
              disabled={!courseToAdd || programsForSelectedCourse.length === 0}
              options={programsForSelectedCourse.map((p) => ({
                value: p.id,
                // Programs without a code (e.g. duplicates) fall back to the
                // title so the option is never blank.
                label: p.program_code ?? p.title,
              }))}
            />
          </div>
          {!canAddMore ? (
            <Typography.Text
              type="secondary"
              style={{ display: "block", marginTop: 8 }}
            >
              Alcanzaste el máximo de 3 cursos relevantes.
            </Typography.Text>
          ) : null}
        </Modal>
      </div>

      {/* Vinculación NEM Primaria */}
      {initialValues.subject_id !== 4 && initialValues.subject_id !== 5 && (
        <div className="selectRow" style={{ marginTop: 24 }}>
          <Title level={4}>Vinculación NEM Primaria</Title>

          {nemPrimaryLinkings.map((linking, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <Select
                placeholder="Libro NEM"
                value={linking.book}
                onChange={(value: NemPrimaryBook) =>
                  setNemPrimaryLinkings(
                    nemPrimaryLinkings.map((l, i) =>
                      i === index ? { ...l, book: value } : l,
                    ),
                  )
                }
                style={{ width: 200 }}
                options={[
                  { value: "AULA", label: "Aula" },
                  { value: "ESCOLARES", label: "Escolares" },
                  { value: "COMUNITARIOS", label: "Comunitarios" },
                  { value: "NUESTROS_SABERES", label: "Nuestros saberes" },
                  { value: "NUMEROS", label: "Números" },
                ]}
              />
              <Input
                placeholder="Páginas (ej. 15-19)"
                value={linking.pages}
                onChange={(e) =>
                  setNemPrimaryLinkings(
                    nemPrimaryLinkings.map((l, i) =>
                      i === index ? { ...l, pages: e.target.value } : l,
                    ),
                  )
                }
                style={{ width: 160 }}
              />
              <Input
                placeholder="Título de la secuencia NEM"
                value={linking.title}
                onChange={(e) =>
                  setNemPrimaryLinkings(
                    nemPrimaryLinkings.map((l, i) =>
                      i === index ? { ...l, title: e.target.value } : l,
                    ),
                  )
                }
                style={{ flex: 1 }}
              />
              <Button
                type="text"
                danger
                onClick={() =>
                  setNemPrimaryLinkings(
                    nemPrimaryLinkings.filter((_, i) => i !== index),
                  )
                }
              >
                Eliminar
              </Button>
            </div>
          ))}

          <Button
            type="dashed"
            onClick={() =>
              setNemPrimaryLinkings([
                ...nemPrimaryLinkings,
                { book: "AULA", pages: "", title: "" },
              ])
            }
            style={{ marginTop: 8 }}
          >
            + Agregar nueva vinculación
          </Button>
        </div>
      )}
    </>
  );
};

export default NormalCourseForm;
